import { toRaw } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'

import { apiClient } from "@/helpers/api.js"
import { useGlobalStore } from '@/stores/GlobalStore'
import { useUserStore } from '@/stores/UserStore'
import { useCreateBatchStore } from "@/stores/CreateBatchStore"
import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'
import { buildPagination } from '@/helpers/pagination'
import { setToLocalStorageByUserId } from '@/helpers/general'
import { fetchMarketPricesInto } from '@/helpers/marketPrices.js'
import { handleApiError, describeRequestError, isBrowserAbortedRequest } from '@/services/errorHandler'
import {
  BATCH_CARD_COLUMN_OPTIONS,
  VIEW_OPTIONS,
  BATCH_DETAILS_VIEW_OPTIONS,
  DISCLOSURES,
  DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS,
  BATCH_TYPE_LABEL
} from '@/constants'
import { useQRCode } from '@vueuse/integrations/useQRCode'
import { reorderColumnOptions, columnOptionsInGridOrder, saveColumnOrder } from '@/helpers/inventory.js'
import { isAwayFromBatch } from '@/helpers/batch.js'

// Bumped by every `fetchBatch` call, so a response can tell whether it is still
// the latest one asked for.
let batchRequestId = 0

export const useBatchStore = defineStore('BatchStore', {
  state: () => {
    return {
      apiBaseUrl: import.meta.env.VITE_API_HOST,
      batchProcessorBaseUrl: import.meta.env.VITE_BATCH_PROCESSOR_API_HOST,
      gridApi: null,

      batches: [],
      pagination: null,

      // Sidebar "Recent Batches". A SEPARATE slot from `batches` on purpose:
      // that one is owned by the All Batches page and is re-sorted, filtered
      // and paginated by it, so sharing it would make the sidebar flicker
      // through whatever the page happens to be showing.
      recentBatches: [],
      recentBatchesLoading: false,

      indexLoading: true,

      batchKind: '',

      selectedBatch: null,
      // What the last current `fetchBatch` failure was (`describeRequestError`),
      // for the failure screen. Cleared when the batch loads.
      batchLoadError: null,
      selectedBatchId: null,
      selectedBatchName: null,

      // Market-price recommendations for the batch cards list (see
      // `fetchMarketPrices`). Mirrors InventoryStore's fields; both drive the
      // shared `@/helpers/marketPrices` loader.
      marketPrices: null,
      currentMarketPriceController: null,
    
      uploads: [],
      countOfUploadedImagesFromMobile: 0,

      notificationStatus: {},
      
      columnOptions: [...BATCH_CARD_COLUMN_OPTIONS],

      viewOptions: [...VIEW_OPTIONS],
      viewOption: { ...VIEW_OPTIONS[0] },
      selectedBatchDetailsViewOption: BATCH_DETAILS_VIEW_OPTIONS[0].value,

      disclosures: { ...DISCLOSURES },

      isAllFilterItem: false,

      openedPanel: false,

      settings: {
        name: '',
        ...DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS
      },

      batchType: 'sports',

      recentlyUploadedImagesIds: [],

      inspectorExpandedErrorBlock: false,

      rowNode: null,

      qrcodeLink: `${import.meta.env.VITE_MAIN_SITE}/mobile-scan`,
      otpPassword: '',
      generateOtpPasswordLoader: false,
      otpBatchId: null,

      openBatchSettings: false,
    }
  },

  getters: {
    /**
     * Is the batch on screen one the backend CREATED FROM AN IMPORT
     * (eBay / CollX / Shopify / CSV) rather than from scanning or uploading?
     *
     * The app never makes these — the backend does, stamping `is_import` on
     * them. They are named for their source and timestamp
     * (`EBAY 2026-09-23 14:30`), and a large import is split into batches of
     * 500 (`… -1`, `… -2`), so a dealer can be looking at part three of seven.
     *
     * It matters because the batch TOOLS all assume a Magic Scan batch:
     * reprocessing unmatched cards, adding a banner, adding more cards, and the
     * batch-wide type / sport / locked-set settings describe how images become
     * cards. An import has no images to reprocess and no scan still to come, so
     * those controls are removed rather than left to fail.
     *
     * Every surface that strips a tool asks THIS getter — the menu, the
     * settings dialog, the header's add-cards row and the add flow itself — so
     * they cannot disagree about which batch is which. A batch that never
     * loaded, or a backend without the field, reads as NOT an import: the full
     * tool set, which is the behaviour from before imports created batches.
     */
    isImportBatch() {
      return !!this.selectedBatch?.is_import
    },

    selectedViewOption() {
      if (!this.viewOption) return
      const userStore = useUserStore()
      const savedBatchDetailsViewOption = JSON.parse(localStorage.getItem('batchDetailsViewOption'))

      return (savedBatchDetailsViewOption && savedBatchDetailsViewOption[userStore.user?.id]) || this.viewOption
    },
  },

  actions: {
    setBatchStoreFields(fields) {
      for (const [field, value] of Object.entries(fields)) {
        this[field] = value
      }
    },

    updateViewOptions(availableViewOptions) {
      if (availableViewOptions.length) {
        this.viewOptions = VIEW_OPTIONS.reduce((acc, item) => {
          if (availableViewOptions.includes(item.value)) {
            acc.push(item)
          }
          return acc
        }, [])

        this.viewOptions = [VIEW_OPTIONS[0], ...this.viewOptions]
      } else {
        this.viewOptions = []
      }
    },

    updateColumnOption(option) {
      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      this.columnOptions = this.columnOptions.reduce((acc, item) => {
        item.field === option.field ? acc.push({...item, checked: !item.checked}) :  acc.push(item)

        return acc
      }, [])

      // Persist under the active namespace key. For the batch page this is
      // 'display_columns' (unchanged); for batch-in-inventory it is
      // 'display_columns_inventoryBatch' so column toggles never touch the batch.
      const columnsKey = globalStore.saveActiveFilterFor?.displayColumns || 'display_columns'
      setToLocalStorageByUserId(this.columnOptions, columnsKey, userStore.user.id)
    },

    /**
     * Reorder the columns from the Columns dropdown (drag or Alt+arrows).
     * Takes FIELDS, not indexes — hidden rows make indexes unreliable.
     * Same namespaced key as `updateColumnOption`, so a batch and
     * batch-in-inventory keep separate arrangements.
     */
    moveColumnOption(fromField, toField) {
      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      this.columnOptions = reorderColumnOptions(this.columnOptions, fromField, toField)
      saveColumnOrder(this.columnOptions.map(col => col.field), useUserStore().user?.id)

      const columnsKey = globalStore.saveActiveFilterFor?.displayColumns || 'display_columns'
      setToLocalStorageByUserId(this.columnOptions, columnsKey, userStore.user.id)
    },

    /**
     * Mirror a header drag in the grid back into the dropdown order, so the two
     * surfaces never disagree. No-op when nothing moved.
     */
    syncColumnOptionsOrder(orderedFields) {
      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      const next = columnOptionsInGridOrder(this.columnOptions, orderedFields)
      if (next === this.columnOptions) return

      this.columnOptions = next
      saveColumnOrder(this.columnOptions.map(col => col.field), useUserStore().user?.id)

      const columnsKey = globalStore.saveActiveFilterFor?.displayColumns || 'display_columns'
      setToLocalStorageByUserId(this.columnOptions, columnsKey, userStore.user.id)
    },

    updateSelectedBatch({data = this.selectedBatch, config = {}}) {
      const configStore = useConfigStore()
      const configuration = configStore.config || config

      if (data) {
        this.selectedBatch = data
        this.selectedBatchId = data.id
      }

      if (data && configuration && Object.keys(configuration).length) {
        const sportsAndGamesBasedOnType = data.type === 'tcg'
          ? configuration.games.find(item => item.collx_type_id === data.collx_type_id)
          : configuration[data.type].find(item => item.collx_type_id === data.collx_type_id)

        const titleTemplateKey = Object.keys(configuration.titleTemplates).find(key => configuration.titleTemplates[key].id === data.title_template_id)
        const descriptionTemplateKey = Object.keys(configuration.descriptionTemplates).find(key => configuration.descriptionTemplates[key].id === data.description_template_id)
        this.settings = {
          name: data.name,
          titleTemplates: configuration.titleTemplates[titleTemplateKey] ||  { id: null, name: 'Default CDP Template' },
          descriptionTemplates: configuration.descriptionTemplates[descriptionTemplateKey] || { id: null, name: 'Default CDP Template' },
          sportsAndGamesBasedOnType: sportsAndGamesBasedOnType || { collx_type_id: null, name: data.type === 'tcg' ? 'Assorted Games' : 'Assorted Sports' },
          batchTypes: { name: BATCH_TYPE_LABEL[data.type], id: data.type },
        }

        this.batchType = data.type
      }
    },

    /**
     * @param {object} [options.itemFiltersAtLeave] the raw `itemFilters` object
     *   the leaving page held when the route left it. The next page's setup
     *   runs BEFORE this page's `onUnmounted`, so by the time this is called
     *   the shared filters may already be the next page's; they are cleared
     *   only if they are still the very object this page left behind. Omitted
     *   (no route leave seen), they are cleared as before.
     */
    updateToDefault({ itemFiltersAtLeave } = {}) {
      const createBatchStore = useCreateBatchStore()
      const cardStore = useCardStore()

      this.viewOptions = VIEW_OPTIONS
      this.columnOptions = BATCH_CARD_COLUMN_OPTIONS
      this.selectedBatchDetailsViewOption = BATCH_DETAILS_VIEW_OPTIONS[0].value
      this.selectedBatch = null
      this.selectedBatchId = null
      this.settings = {
        name: '',
        ...DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS
      }

      cardStore.selectedCardId = null
      cardStore.pinnedCardData = {}
      if (itemFiltersAtLeave === undefined || toRaw(cardStore.itemFilters) === itemFiltersAtLeave) {
        cardStore.itemFilters = {}
      }
      cardStore.selectedRanges = null
      cardStore.selectedCards = {}

      createBatchStore.batchType = 'sports'
      createBatchStore.selectedLockSet = null
    },

    /**
     * @param {string} query  SORT key (`createdAt.desc`) — not a search term.
     * @param {string} search Server-side name search. `GET /batches` accepts
     *   `q`, which is what global search already uses; without it a panel can
     *   only filter the pages it happens to have loaded, so a batch on page 2
     *   was simply unfindable.
     */
    async fetchBatches({ page = 1, status = null, query = 'createdAt.desc', search = '' }) {
      // Deliberately does NOT cancel the inventory request in flight. The
      // batches list shares nothing with it, and Inventory surfaces (the All
      // Batches panel, the batch breadcrumb, a Pusher refresh) call this while
      // their own grid is loading — the cancel left that grid spinning.
      this.indexLoading = true

      let url = `/batches?page=${page}&sortBy=${query}`
      if (status) {
        url += `&status=${status}`
      }

      const term = String(search || '').trim()
      if (term) {
        url += `&q=${encodeURIComponent(term)}`
      }

      try {
        const response = await apiClient().get(url)

        const data = response.data

        this.batches = data?.items || []

        this.pagination = buildPagination({ data, totalItemsField: 'total_items' })

        return data.items
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'fetchBatches' })
      } finally {
        this.indexLoading = false
      }
    },

    /**
     * The 3 (by default) most recently created batches for the sidebar.
     *
     * Same `GET /batches` endpoint the All Batches page uses — no new BE work —
     * but the response goes to its own slot and never touches `pagination`.
     */
    // 6, not 3: the sidebar shows 3 and the Inventory strip 4, and both read
    // this one slot. Keeping the wider set here means neither can starve the
    // other depending on which mounted first.
    async fetchRecentBatches({ limit = 6 } = {}) {
      this.recentBatchesLoading = true

      try {
        const { data } = await apiClient().get(`/batches?page=1&sortBy=createdAt.desc`)
        this.recentBatches = (data?.items || []).slice(0, limit)

        return this.recentBatches
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'fetchRecentBatches' })
        return []
      } finally {
        this.recentBatchesLoading = false
      }
    },

    fetchPrevPage() {
      if (this.pagination?.prevPage) {
        this.fetchBatches(this.pagination?.prevPage)
      }
    },
    fetchNextPage() {
      if (this.pagination?.nextPage) {
        this.fetchBatches(this.pagination?.nextPage)
      }
    },

    /**
     * Load a batch into `selectedBatch`.
     *
     * The latest call wins: switching A → B fires two of these, and when A's
     * answer arrived second it was written over B — the page then sat on batch
     * B's URL and cards under batch A's name, settings and row key.
     *
     * @returns {Promise<boolean|undefined>} `true` loaded, `false` failed,
     *   `undefined` superseded by a newer call (which reports for itself),
     *   answered after the user left the batch, or dropped by the browser.
     */
    async fetchBatch(batchId, { afterBrowserAbort = false } = {}) {
      const createBatchStore = useCreateBatchStore()

      batchRequestId = batchRequestId + 1
      const requestId = batchRequestId

      try {
        const { data } = await apiClient().get(`/batches/${batchId}`)

        if (requestId !== batchRequestId) return

        // The user left this batch while it loaded. Writing it now would
        // re-select a batch nobody is on, and every `selectedBatchId` reader
        // after that (Pusher refreshes included) would act on it.
        if (isAwayFromBatch(batchId)) return

        createBatchStore.batchInfo = {
          collx_type_id: data.collx_type_id
        }

        this.updateSelectedBatch({ data })
        this.batchLoadError = null

        return true
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'fetchBatch' })

        if (requestId !== batchRequestId) return

        // The browser dropped the request — no server answered, so this is
        // not a failed load and must not raise the "couldn't load" screen.
        // Asked once more for the batch still on screen; a second abort
        // reports nothing either way.
        if (isBrowserAbortedRequest(error)) {
          if (afterBrowserAbort) return

          await new Promise(resolve => setTimeout(resolve, 1000))

          if (requestId !== batchRequestId || isAwayFromBatch(batchId)) return

          return this.fetchBatch(batchId, { afterBrowserAbort: true })
        }

        this.batchLoadError = describeRequestError(error)

        return false
      }
    },

    // Market prices for the batch cards list. Same contract as Inventory
    // (`{ ok, recommendations }` keyed by card id) — only the endpoint differs,
    // so it delegates to the shared loader. Pass the SAME `query` the cards list
    // used (`fetchSelectedCards`) so prices line up with the current page/filters.
    async fetchMarketPrices({ batchId, query } = {}) {
      const id = batchId || this.selectedBatchId
      if (!id) return

      await fetchMarketPricesInto(this, {
        url: `/batches/${id}/market-prices`,
        params: query,
        context: 'BatchStore',
      })
    },

    async updateBatch(body) {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      try {
        const { data } = await apiClient().put(`/batches/${this.selectedBatchId}`, body)

        this.batches = this.batches.reduce((acc, element) => {
          if (element.id === data.id) {
            acc.push({ ...element, ...body })
          } else {
            acc.push(element)
          }

          return acc
        }, [])

        if (this.selectedBatch) {
          this.updateSelectedBatch({ data: {...data} })
        }
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'updateBatch' })
      }
    },

    /**
     * Rename ONE batch by id, without touching `selectedBatchId`.
     *
     * `updateBatch` above writes to whatever batch is currently selected, which
     * is right for the batch-details page but wrong for a list: renaming row 12
     * must not depend on, or move, which batch the rest of the app thinks is
     * open. Returns a boolean so an optimistic row can roll its own value back —
     * `updateBatch` reports nothing, and a rename that silently failed would
     * leave the grid showing a name the server never accepted.
     */
    async renameBatch(batchId, name) {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return false
      if (!batchId || !name) return false

      try {
        await apiClient().put(`/batches/${batchId}`, { name })

        this.batches = this.batches.map((batch) =>
          batch.id === batchId ? { ...batch, name } : batch
        )

        if (this.selectedBatch?.id === batchId) {
          this.selectedBatch = { ...this.selectedBatch, name }
        }

        return true
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'renameBatch' })

        return false
      }
    },

    toggleBatchStatus(batch) {
      this.selectedBatchId = batch.id
      this.updateBatch({ status: batch.status === 'open' ? 'closed' : 'open' })
    },

    async reprocessBatch() {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      try {
        globalStore.cellsLoading = true

        if (this.selectedBatchDetailsViewOption !== 'Overview') {
          cardStore.cardsLoading = true
        }

        const data = await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/reprocess`)
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'reprocessBatch' })
      }
    },

    async addBannerToBatch() {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()

      try {
        await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/add-banner?banner=${userStore.user.banner_image}&location=${userStore.user?.banner_placement || 'top'}`)
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'addBannerToBatch' })
      }
    },

    async removeBannerFromBatch() {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()

      try {
        await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/remove-banner?banner=${userStore.user.banner_image}`)
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'removeBannerFromBatch' })
      }
    },

    /**
     * Delete the selected batch and take it out of every list that holds it.
     *
     * The id is captured BEFORE the await on purpose. Callers navigate away the
     * moment this is called, which unmounts the batch page, and its
     * `onUnmounted` runs `updateToDefault()` — nulling `selectedBatchId` long
     * before a network round-trip resolves. Reading the id after the await then
     * compared every row against `null`, matched nothing, and pruned nothing;
     * the only reason it looked right is that both destinations happened to
     * refetch on mount.
     *
     * `recentBatches` is pruned too. It is a SEPARATE slot from `batches` (the
     * sidebar's Recent rail and Inventory's recent-batch chips read it), and
     * both consumers only fetch it when it is EMPTY — so a deleted batch stayed
     * a clickable chip until a hard reload, and clicking it opened a nameless
     * batch page with an empty grid.
     *
     * @returns {Promise<boolean>} whether the delete actually succeeded, so a
     *   caller can avoid navigating the user to a list that still has the batch.
     */
    async removeBatch() {
      const id = this.selectedBatchId

      if (!id) return false

      try {
        await apiClient().delete(`/batches/${id}`)

        this.batches = this.batches.filter(item => item.id !== id)
        this.recentBatches = this.recentBatches.filter(item => item.id !== id)

        // Owned here rather than by each caller: the legacy list decremented it
        // itself and the new batches page didn't, so its pagination bar kept
        // reporting the old total.
        if (this.pagination?.totalItems > 0) this.pagination.totalItems--

        return true
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'removeBatch' })

        return false
      }
    },

    async getOtpPassword(batchId) {
      try {
        this.generateOtpPasswordLoader = true
        this.otpBatchId = batchId
        const { data } = await apiClient().post(`otp-links`, {
          redirect_to: `/mobile-scan/${batchId}`
        })

        if (data?.ok) {
          const splitLoginUrl = data.otp_login_url.split('/')
          const url = `${import.meta.env.VITE_MAIN_SITE}/otp-links/${splitLoginUrl[splitLoginUrl.length - 1]}`

          this.qrcodeLink = useQRCode(url)
        }

        this.generateOtpPasswordLoader = false
      } catch (error) {
        this.generateOtpPasswordLoader = false
        handleApiError(error, { component: 'BatchStore', action: 'getOtpPassword' })
      }
    },
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBatchStore, import.meta.hot))
}
